import { gsap, Linear, Power4 } from 'gsap'
import React, { useRef, useState } from 'react'
// Custom styles for component
import styles from './SwiperBook.module.scss'

// Swiper dependencies
import SwiperCore from 'swiper'
import { Keyboard, Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

// Children
import SwiperBookLayers from './SwiperBookLayers'
import SwiperBookPagination from './SwiperBookPagination'

// Add swiper modules
SwiperCore.use([Navigation, Keyboard])

// TS
interface chapterProps {
  id: string
  title: string
}

const SwiperBook = ({ content }: any) => {
  // Store the Swiper instance
  const [SwiperInstance, setSwiperInstance] = useState<SwiperCore>()
  const [swiperProgress, setSwiperProgress] = useState<number>(0)

  // Create array of refs from the slides array
  const pages: any = []
  content.forEach((chapter: any) => {
    chapter.pages.forEach((page: any) => {
      pages.push(page)
    })
  })

  // Create swiper refs to get current slide
  const swiperRefs = useRef(pages.map(() => React.createRef()))

  const initSwiper = (swiper: any) => {
    const currentIndex = swiper.realIndex
    const $currentLayersEl = document.getElementsByClassName(
      'swiper-slide-active',
    )

    if (!$currentLayersEl.length) return

    actionsController(
      'video',
      'play',
      currentIndex,
      $currentLayersEl[0].querySelectorAll('video'),
    )
  }

  // Swiper controller for play/pause and other events
  const onSlideChange = (swiper: any) => {
    // Get current and previous slide index
    const currentIndex = swiper.realIndex
    const previousIndex = currentIndex - 1

    setSwiperProgress(((swiper.realIndex + 1) / pages.length) * 100)

    if (!swiperRefs?.current || !swiperRefs) return

    // Get current and prev DOM element
    const $currentLayersEL = swiperRefs.current[currentIndex].current
    const $previousLayersEL = swiperRefs.current[previousIndex]?.current

    // Check active slide's chapter id to update the navigation
    const chapterID = $currentLayersEL.dataset.chapterId
    const chapterTitle = $currentLayersEL.dataset.chapterTitle

    if (chapterID) setActiveNavItem({ id: chapterID, title: chapterTitle })

    // Play videos based on current slide
    actionsController(
      'video',
      'play',
      currentIndex,
      $currentLayersEL.querySelectorAll('video'),
    )

    // Pause prev videos
    if (previousIndex >= 0) {
      actionsController(
        'video',
        'pause',
        previousIndex,
        $previousLayersEL.querySelectorAll('video'),
      )
    }

    // Reset all SVG and Graphics layers
    actionsController(
      'graphics',
      'set',
      currentIndex,
      $currentLayersEL.querySelectorAll('.Layer--svg, .Layer--graphics'),
    )

    // Reset all image layers
    actionsController(
      'images',
      'set',
      currentIndex,
      $currentLayersEL.querySelectorAll('.Layer--image'),
    )
  }

  const onslideChangeTransitionEnd = (swiper: any) => {
    const currentIndex = swiper.realIndex
    // Get active DOM element
    const $currentLayersEL = swiperRefs.current[currentIndex].current

    // Fade all graphics in
    actionsController(
      'graphics',
      'animate',
      currentIndex,
      $currentLayersEL.querySelectorAll('.Layer--svg, .Layer--graphics'),
    )

    // Scale all images
    actionsController(
      'images',
      'animate',
      currentIndex,
      $currentLayersEL.querySelectorAll('.Layer--image'),
    )
  }

  const navController = (direction: string) => {
    if (direction === 'next') {
      SwiperInstance?.slideNext()
    } else {
      SwiperInstance?.slidePrev()
    }
  }

  const [ActiveNavItem, setActiveNavItem] = useState<chapterProps>({
    id: content[0].chapterID,
    title: content[0].chapterTitle,
  })

  // Gets called from SwiperBookPagination component
  const onNavItemClick = (chapter: {
    startIndex: number
    chapterID: string
    chapterTitle: string
  }) => {
    SwiperInstance?.slideTo(chapter.startIndex + 1, 0, undefined)
    setActiveNavItem({ id: chapter.chapterID, title: chapter.chapterTitle })
  }

  return (
    <div className={styles.SwiperContainer}>
      <div className={styles.SwiperBookIndication}>
        <span
          className="progress"
          style={{ width: `${swiperProgress}%` }}
        ></span>
      </div>
      <SwiperBookPagination
        items={content}
        jumpToChapter={onNavItemClick}
        triggerText={ActiveNavItem}
      />

      <Swiper
        className={`${styles.SwiperBook} swiper-book`}
        slidesPerView={1}
        spaceBetween={0}
        //@ts-ignore
        preloadImages={false}
        loop={true}
        lazy={{ enabled: true, loadPrevNext: true, loadPrevNextAmount: 2 }}
        onSwiper={setSwiperInstance}
        onInit={initSwiper}
        onSlideChange={(swiper) => onSlideChange(swiper)}
        onSlideChangeTransitionEnd={(swiper) =>
          onslideChangeTransitionEnd(swiper)
        }
        keyboard={{ enabled: true }}
      >
        {content.map((chapter: any, index: number) =>
          chapter.pages.map((page: any, chapterIndex: number) => (
            <SwiperSlide
              className={`${styles.SwiperSlide} ${page.pageNumber}`}
              key={chapterIndex}
            >
              <SwiperBookLayers
                chapter={chapter}
                page={page}
                pageRef={swiperRefs.current[page.pageNumber - 1]}
              />
            </SwiperSlide>
          )),
        )}
      </Swiper>

      {/* Prev/next */}
      <div className={styles.SwiperNavigation}>
        <div
          onClick={() => navController('prev')}
          className={styles.SwiperNavigationControls}
        ></div>
        <div
          onClick={() => navController('next')}
          className={`${styles.SwiperNavigationControls} ${styles.SwiperNavigationControlsNext}`}
        ></div>
      </div>
    </div>
    // </div>
  )
}

export default SwiperBook

//Helpers

function actionsController(
  type: string,
  action: string,
  index: number,
  $els: any,
) {
  switch (type) {
    case 'video':
      videoController(action, $els)
      break

    case 'graphics':
      if (!$els.length) return

      if (action === 'set') {
        gsap.set($els, { opacity: 0 })
      } else {
        gsapController($els, {
          duration: 1,
          opacity: 1,
          ease: Power4.easeInOut,
          stagger: 0.6,
        })
      }

      break

    case 'images':
      if (!$els.length) return

      for (let i = 0; i < $els.length; i++) {
        const $el = $els[i]
        const zoom = $el.dataset.layerZoom

        if (zoom === 'true') {
          if (action === 'set') {
            gsap.set($el, { scale: 1 })
          } else {
            gsapController($el, {
              duration: 10,
              delay: 1,
              scale: 1.15,
              ease: Linear.easeNone,
            })
          }
        }
      }
      break
  }
}

// Controller for video on slide change
function videoController(action: string, videos: []) {
  if (!videos?.length) return

  for (let i = 0; i < videos.length; i++) {
    const $video: any = videos[i]

    action === 'play' ? $video.play() : $video.pause()
  }
}

// Gsap controller
function gsapController(els: [], aniProps: {}) {
  gsap.to(els, aniProps)
}
